/**
 * Glass sidebar with SwitchConnect brand, navigation, and support CTA.
 * Nav items filtered by user role per BL-002.
 */

import { useCallback, useMemo, useState, type ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard,
  Building2,
  Users,
  ShieldCheck,
  ShieldAlert,
  ScrollText,
  Settings,
  Settings2,
  Link2,
  Clock,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/brand/logo";
import { PortalToggle } from "@/components/layout/portal-toggle";
import { SearchBox } from "@/components/layout/search-box";
import { UserCard } from "@/components/layout/user-card";
import { SupportCTA } from "@/components/brand/support-cta";
import { useRole, type UserRole } from "@/hooks/use-role";
import { useAuth } from "@/hooks/use-auth";

interface NavItem {
  readonly label: string;
  readonly icon: ReactNode;
  readonly href: string;
  readonly roles: readonly UserRole[];
}

const NAV_ITEMS: readonly NavItem[] = [
  { label: "Dashboard", icon: <LayoutDashboard size={20} />, href: "/dashboard", roles: ["admin", "technician"] },
  { label: "Customers", icon: <Building2 size={20} />, href: "/customers", roles: ["admin", "technician"] },
  { label: "Technicians", icon: <Users size={20} />, href: "/technicians", roles: ["admin"] },
  { label: "PIM Sessions", icon: <ShieldCheck size={20} />, href: "/pim-sessions", roles: ["admin", "technician"] },
  { label: "PIM Approvals", icon: <ShieldAlert size={20} />, href: "/pim-approvals", roles: ["admin", "technician"] },
  { label: "GDAP Relationships", icon: <Link2 size={20} />, href: "/gdap", roles: ["admin"] },
  { label: "Audit Log", icon: <ScrollText size={20} />, href: "/audit-log", roles: ["admin", "auditor"] },
  { label: "Approval Policies", icon: <Settings2 size={20} />, href: "/approval-policies", roles: ["admin"] },
  { label: "Scheduled Tasks", icon: <Clock size={20} />, href: "/scheduled-tasks", roles: ["admin"] },
  { label: "Settings", icon: <Settings size={20} />, href: "/settings", roles: ["admin"] },
];

const ROLE_LABELS: Record<UserRole, string> = {
  admin: "Administrator",
  technician: "Technician",
  auditor: "Auditor",
};

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [portalMode, setPortalMode] = useState<"admin" | "customer">("admin");
  const role = useRole();
  const { displayName } = useAuth();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  const filteredNavItems = useMemo(
    () => NAV_ITEMS.filter((item) => item.roles.includes(role)),
    [role],
  );

  // Build user initials from display name
  const initials = useMemo(() => {
    if (!displayName) return "??";
    const parts = displayName.split(" ").filter(Boolean);
    if (parts.length >= 2) return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
    return displayName.slice(0, 2).toUpperCase();
  }, [displayName]);

  return (
    <aside
      className={cn(
        "flex flex-col min-h-screen transition-all duration-200",
        "bg-sc-sidebar backdrop-blur-[20px] border-r border-sc-sidebar-border",
        collapsed ? "w-16" : "w-64",
      )}
    >
      {/* Logo + collapse toggle */}
      <div className="flex items-center justify-between p-4 border-b border-sc-border">
        <Logo collapsed={collapsed} />
        <button
          onClick={toggleCollapsed}
          className="p-1 rounded-btn text-sc-text-muted hover:text-sc-text hover:bg-sc-surface-hover transition-colors"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {/* Portal toggle + search (admin only) */}
      {!collapsed && role === "admin" && (
        <div className="px-4 pt-4 space-y-3">
          <PortalToggle active={portalMode} onToggle={setPortalMode} />
          <SearchBox />
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 mt-2">
        {filteredNavItems.map((item) => {
          const isActive = currentPath === item.href || currentPath.startsWith(item.href + "/");
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "flex items-center gap-3 w-full px-3 py-2 rounded-btn text-sm font-medium transition-colors relative",
                isActive
                  ? "bg-sc-green-soft text-sc-green font-semibold"
                  : "text-sc-text-secondary hover:text-sc-text hover:bg-sc-surface-hover",
                collapsed && "justify-center px-2",
              )}
              aria-current={isActive ? "page" : undefined}
              title={collapsed ? item.label : undefined}
            >
              {/* Green left border for active state */}
              {isActive && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-sc-green shadow-sc-glow" />
              )}
              {item.icon}
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Bottom section: Support CTA + User card */}
      {!collapsed && (
        <div className="px-3 pb-3 space-y-3">
          <SupportCTA />
          <UserCard
            initials={initials}
            name={displayName ?? "User"}
            role={ROLE_LABELS[role]}
          />
        </div>
      )}
    </aside>
  );
}
